import { dollars, Size, type SizeId } from './buildings';

/** Wire and save ids for aircraft classes. Append only. */
export const AircraftClass = {
  Regional: 1,
  Narrowbody: 2,
  Widebody: 3,
  Light: 4,
  /** Military (see the military zone in buildings.ts): no passengers, the ministry pays. */
  Fighter: 5,
  Airlifter: 6,
  Drone: 7,
  /** A private helicopter: lands on a helipad, no runway or stand (see the helipad rules). */
  Helicopter: 8,
  /** A small single-engine aircraft (a flying club's, a pilot's own): any runway and stand. */
  Ga: 9,
  /** A jumbo: four engines, two decks; only a large runway and a large stand take it. */
  Jumbo: 10,
  /** A giant freighter (four engines, a nose that opens): large runway and stand only. */
  Giant: 11,
} as const;
export type AircraftClassId = (typeof AircraftClass)[keyof typeof AircraftClass];

/**
 * Where a class parks: an airliner on a stand, a fighter in a hardened shelter, other military
 * aircraft on the military apron. Not saved (derived from the building type).
 */
/**
 * Pad: the de-icing pad, a stand nobody parks on (aircraft call there on their way out). Remote:
 * a remote parking position, where aircraft wait between flights (no services).
 */
export const StandKind = { Civil: 0, Shelter: 1, Military: 2, Pad: 3, Remote: 4 } as const;
export type StandKindId = (typeof StandKind)[keyof typeof StandKind];

export interface AircraftClassDef {
  readonly id: AircraftClassId;
  readonly key:
    | 'regional'
    | 'narrowbody'
    | 'widebody'
    | 'light'
    | 'fighter'
    | 'airlifter'
    | 'drone'
    | 'helicopter'
    | 'ga'
    | 'jumbo'
    | 'giant';
  readonly seats: number;
  /** Cents charged to the airline per landing. */
  readonly landingFee: number;
  /** Ground time at the stand, in game minutes. */
  readonly turnaroundMinutes: number;
  /** Minimum completed stand level. */
  readonly standLevel: number;
  /**
   * Minimum completed runway level: a longer, stronger runway for bigger aircraft. Airlines only
   * offer contracts for aircraft the airport's best runway can take.
   */
  readonly runwayLevel: number;
  /** Ground service durations in game minutes (fuelling doubles without a fuel depot). */
  readonly unloadMinutes: number;
  readonly fuelMinutes: number;
  readonly cleanMinutes: number;
  readonly cateringMinutes: number;
  readonly loadMinutes: number;
  /** Boarding opens this many minutes before the scheduled departure. */
  readonly boardingLeadMinutes: number;
  /** Cents earned per departure from fuel sales when the airport has a fuel depot. */
  readonly fuelMargin: number;
  /** Strongest crosswind (knots) it lands and takes off in. */
  readonly crosswindLimit: number;
  /** Fire station level needed for its rescue and fire-fighting category (ICAO RFFS). */
  readonly fireLevel: number;
  /**
   * Control tower level from which airlines offer contracts for it: a new airfield with a first
   * tower sees private jets only, the airliners come with a bigger tower.
   */
  readonly towerLevel: number;
  /** De-icing time in game minutes (freezing weather). */
  readonly deiceMinutes: number;
  /** A military class: parks in the military zone, carries no passengers. */
  readonly military: boolean;
  readonly parking: StandKindId;
  /** Cents the ministry pays for each sortie (military classes). */
  readonly militaryFee: number;
  /** The smallest runway that takes it (see Size). */
  readonly size: SizeId;
  /**
   * The smallest stand and parking position that take it: its size, but large for a widebody,
   * which lands on a level 4 (medium) runway but does not fit a medium stand.
   */
  readonly standSize: SizeId;
  /** A big aircraft: may use a large runway or stand kept for the big ones (Building.bigOnly). */
  readonly big: boolean;
}

export const AIRCRAFT_CLASSES: Readonly<Record<AircraftClassId, AircraftClassDef>> = {
  [AircraftClass.Regional]: {
    id: AircraftClass.Regional,
    key: 'regional',
    seats: 72,
    landingFee: dollars(900),
    turnaroundMinutes: 30,
    standLevel: 1,
    runwayLevel: 1,
    unloadMinutes: 6,
    fuelMinutes: 8,
    cleanMinutes: 5,
    cateringMinutes: 6,
    loadMinutes: 6,
    boardingLeadMinutes: 20,
    fuelMargin: dollars(450),
    crosswindLimit: 25,
    fireLevel: 1,
    deiceMinutes: 8,
    towerLevel: 2,

    military: false,
    parking: StandKind.Civil,
    militaryFee: 0,
    size: Size.Medium,
    standSize: Size.Medium,
    big: false,
  },
  [AircraftClass.Narrowbody]: {
    id: AircraftClass.Narrowbody,
    key: 'narrowbody',
    seats: 170,
    landingFee: dollars(1_800),
    turnaroundMinutes: 45,
    standLevel: 1,
    runwayLevel: 2,
    unloadMinutes: 10,
    fuelMinutes: 12,
    cleanMinutes: 8,
    cateringMinutes: 10,
    loadMinutes: 10,
    boardingLeadMinutes: 30,
    fuelMargin: dollars(950),
    crosswindLimit: 30,
    fireLevel: 1,
    deiceMinutes: 12,
    towerLevel: 2,

    military: false,
    parking: StandKind.Civil,
    militaryFee: 0,
    size: Size.Medium,
    standSize: Size.Medium,
    big: false,
  },
  [AircraftClass.Widebody]: {
    id: AircraftClass.Widebody,
    key: 'widebody',
    seats: 310,
    landingFee: dollars(4_200),
    turnaroundMinutes: 90,
    standLevel: 2,
    runwayLevel: 3,
    unloadMinutes: 20,
    fuelMinutes: 25,
    cleanMinutes: 15,
    cateringMinutes: 20,
    loadMinutes: 20,
    boardingLeadMinutes: 40,
    fuelMargin: dollars(2_300),
    crosswindLimit: 34,
    fireLevel: 2,
    deiceMinutes: 20,
    towerLevel: 2,

    military: false,
    parking: StandKind.Civil,
    militaryFee: 0,
    size: Size.Medium,
    standSize: Size.Large,
    big: true,
  },
  // Private and business jets: a handful of passengers, a quick turn, small fees.
  [AircraftClass.Light]: {
    id: AircraftClass.Light,
    key: 'light',
    seats: 10,
    landingFee: dollars(300),
    turnaroundMinutes: 20,
    standLevel: 1,
    runwayLevel: 1,
    unloadMinutes: 3,
    fuelMinutes: 6,
    cleanMinutes: 3,
    cateringMinutes: 3,
    loadMinutes: 3,
    boardingLeadMinutes: 15,
    fuelMargin: dollars(180),
    crosswindLimit: 20,
    // Below the airline categories: a crewed first fire station covers them.
    fireLevel: 1,
    deiceMinutes: 5,
    towerLevel: 1,

    military: false,
    parking: StandKind.Civil,
    militaryFee: 0,
    size: Size.Medium,
    standSize: Size.Medium,
    big: false,
  },
  // Military: no passengers and no passenger services; the ministry pays for every sortie.
  [AircraftClass.Fighter]: {
    id: AircraftClass.Fighter,
    key: 'fighter',
    seats: 0,
    landingFee: 0,
    turnaroundMinutes: 35,
    standLevel: 1,
    runwayLevel: 2,
    unloadMinutes: 0,
    fuelMinutes: 10,
    cleanMinutes: 0,
    cateringMinutes: 0,
    // Arming.
    loadMinutes: 12,
    boardingLeadMinutes: 0,
    fuelMargin: dollars(700),
    crosswindLimit: 25,
    fireLevel: 1,
    deiceMinutes: 6,
    towerLevel: 4,
    military: true,
    parking: StandKind.Shelter,
    militaryFee: dollars(2_600),
    size: Size.Medium,
    standSize: Size.Medium,
    big: false,
  },
  [AircraftClass.Airlifter]: {
    id: AircraftClass.Airlifter,
    key: 'airlifter',
    seats: 0,
    landingFee: 0,
    turnaroundMinutes: 55,
    standLevel: 1,
    runwayLevel: 2,
    unloadMinutes: 14,
    fuelMinutes: 15,
    cleanMinutes: 0,
    cateringMinutes: 0,
    loadMinutes: 14,
    boardingLeadMinutes: 0,
    fuelMargin: dollars(1_300),
    crosswindLimit: 30,
    fireLevel: 1,
    deiceMinutes: 14,
    towerLevel: 5,
    military: true,
    parking: StandKind.Military,
    militaryFee: dollars(3_800),
    size: Size.Medium,
    standSize: Size.Medium,
    big: false,
  },
  [AircraftClass.Drone]: {
    id: AircraftClass.Drone,
    key: 'drone',
    seats: 0,
    landingFee: 0,
    turnaroundMinutes: 40,
    standLevel: 1,
    runwayLevel: 1,
    unloadMinutes: 0,
    fuelMinutes: 6,
    cleanMinutes: 0,
    cateringMinutes: 0,
    loadMinutes: 6,
    boardingLeadMinutes: 0,
    fuelMargin: dollars(90),
    crosswindLimit: 15,
    fireLevel: 1,
    deiceMinutes: 5,
    towerLevel: 4,
    military: true,
    parking: StandKind.Military,
    militaryFee: dollars(1_100),
    size: Size.Medium,
    standSize: Size.Medium,
    big: false,
  },
  // A private helicopter: straight onto its helipad and off it (no runway, no taxiing); its few
  // passengers come by car to the pad and are on board in minutes.
  [AircraftClass.Helicopter]: {
    id: AircraftClass.Helicopter,
    key: 'helicopter',
    seats: 6,
    landingFee: dollars(450),
    turnaroundMinutes: 12,
    standLevel: 1,
    runwayLevel: 0,
    unloadMinutes: 0,
    fuelMinutes: 0,
    cleanMinutes: 0,
    cateringMinutes: 0,
    loadMinutes: 0,
    boardingLeadMinutes: 0,
    fuelMargin: dollars(60),
    crosswindLimit: 35,
    fireLevel: 1,
    deiceMinutes: 0,
    towerLevel: 1,
    military: false,
    parking: StandKind.Civil,
    militaryFee: 0,
    size: Size.Small,
    standSize: Size.Small,
    big: false,
  },
  // A flying club's or a pilot's own single-engine aircraft: two or three on board, a short
  // turn, small fees; any runway and stand take it, a small one suits it best.
  [AircraftClass.Ga]: {
    id: AircraftClass.Ga,
    key: 'ga',
    seats: 3,
    landingFee: dollars(120),
    turnaroundMinutes: 15,
    standLevel: 1,
    runwayLevel: 1,
    unloadMinutes: 2,
    fuelMinutes: 5,
    cleanMinutes: 2,
    cateringMinutes: 0,
    loadMinutes: 2,
    boardingLeadMinutes: 10,
    fuelMargin: dollars(60),
    crosswindLimit: 15,
    fireLevel: 1,
    deiceMinutes: 4,
    towerLevel: 1,
    military: false,
    parking: StandKind.Civil,
    militaryFee: 0,
    size: Size.Small,
    standSize: Size.Small,
    big: false,
  },
  // A jumbo: the most passengers of all, a long turn; a large runway and stand only.
  [AircraftClass.Jumbo]: {
    id: AircraftClass.Jumbo,
    key: 'jumbo',
    seats: 470,
    landingFee: dollars(7_500),
    turnaroundMinutes: 120,
    standLevel: 1,
    runwayLevel: 3,
    unloadMinutes: 28,
    fuelMinutes: 35,
    cleanMinutes: 22,
    cateringMinutes: 28,
    loadMinutes: 28,
    boardingLeadMinutes: 50,
    fuelMargin: dollars(4_200),
    crosswindLimit: 35,
    fireLevel: 2,
    deiceMinutes: 28,
    towerLevel: 4,
    military: false,
    parking: StandKind.Civil,
    militaryFee: 0,
    size: Size.Large,
    standSize: Size.Large,
    big: true,
  },
  // A giant freighter: freight only (freight airlines fly it), the most of it by far.
  [AircraftClass.Giant]: {
    id: AircraftClass.Giant,
    key: 'giant',
    seats: 0,
    landingFee: dollars(9_000),
    turnaroundMinutes: 150,
    standLevel: 1,
    runwayLevel: 3,
    unloadMinutes: 45,
    fuelMinutes: 40,
    cleanMinutes: 5,
    cateringMinutes: 5,
    loadMinutes: 45,
    boardingLeadMinutes: 0,
    fuelMargin: dollars(5_000),
    crosswindLimit: 32,
    fireLevel: 2,
    deiceMinutes: 30,
    towerLevel: 3,
    military: false,
    parking: StandKind.Civil,
    militaryFee: 0,
    size: Size.Large,
    standSize: Size.Large,
    big: true,
  },
};

/** Cents per departing passenger (passenger service charge). */
export const PASSENGER_FEE = dollars(14);
/** Taxi and bus concession per departing passenger, by transit hub level (cents). */
export const TRANSIT_PER_PAX: readonly number[] = [0, 60, 110];
/** Satisfaction points passengers give for arriving by taxi or bus right at the terminal. */
export const TRANSIT_SATISFACTION = 5;
/**
 * Transfer passengers: with more routes, more arriving passengers connect here (percent of the
 * arriving load per passenger contract, up to a cap; at least two routes needed). They need
 * the minimum connection time (by best terminal level) before they can board, and take empty
 * seats on any departure within the window; those still waiting after it missed their
 * connection and are compensated.
 */
export const TRANSFER_PERCENT_PER_ROUTE = 3;
export const TRANSFER_PERCENT_MAX = 30;
export const MIN_CONNECTION_MINUTES: readonly number[] = [60, 60, 50, 45, 40, 35];
export const TRANSFER_WINDOW_MINUTES = 240;
export const MISSED_CONNECTION_COST = dollars(120);
/**
 * Charter flights for an event (a cup final, a festival…): a short contract of several flights
 * a day, nearly full, at a premium passenger charge, never renewed. Event ids are saved: append
 * only (0 = a regular contract).
 */
export const CHARTER_EVENTS = ['cupFinal', 'festival', 'concert', 'fair', 'congress', 'pilgrimage'];
export const CHARTER_PERCENT = 10;
export const CHARTER_DAYS = 2;
export const CHARTER_FEE_PERCENT = 140;
/** Hotel rooms by level, and the nightly room rate (cents) by level. */
export const HOTEL_ROOMS: readonly number[] = [0, 60, 140, 260];
export const HOTEL_ROOM_RATE: readonly number[] = [0, dollars(85), dollars(105), dollars(130)];
/** Guests a night: this share (per mille) of yesterday's passengers, plus crews per contract. */
export const HOTEL_GUESTS_PERMILLE = 25;
export const HOTEL_CREW_ROOMS = 4;
/** Comfort rating bonus (of 1000) with a hotel on a road. */
export const HOTEL_COMFORT = 60;
/**
 * A railway station near a terminal: a concession on every departing passenger (by station
 * level, on top of the taxi and bus station's) and passengers who arrive by train are happier.
 */
export const RAIL_PER_PAX: readonly number[] = [0, 90, 150];
export const RAIL_SATISFACTION = 4;
export const RAIL_COMFORT = 80;
/**
 * Flight kitchen on a road: it sells the airlines a meal for every departing passenger (cents, by
 * level) and its trucks load faster (catering time, percent).
 */
export const CATERING_PER_PAX: readonly number[] = [0, 60, 100];
export const CATERING_TIME_PERCENT = 70;
/**
 * Mishandled baggage (per mille of bags): few with the airport's own, fully crewed handlers,
 * more the shorter the crews, most with the outside contractor. Each lost bag is compensated.
 */
export const BAGS_LOST_PERMILLE = 2;
export const BAGS_LOST_CONTRACTOR_PERMILLE = 12;
export const BAG_COMPENSATION = dollars(60);
/** A transit hub serves a terminal within this many tiles of it. */
export const TRANSIT_REACH = 30;

export interface AirlineDef {
  readonly id: number;
  readonly name: string;
  readonly code: string;
  /** Livery colour (sRGB hex) for rendering. */
  readonly color: number;
  /**
   * Relative weights of the aircraft classes it offers contracts for, by class id − 1
   * (regional, narrowbody, widebody, light, fighter, airlifter, drone, helicopter, small
   * single-engine, jumbo, giant freighter); missing entries are 0.
   */
  readonly fleet: readonly number[];
  /** A freight airline: flies cargo only, to airports with a cargo terminal. */
  readonly cargo?: boolean;
  /** Flies international routes: needs passport control (terminal level 2). */
  readonly international?: boolean;
  /** A military unit (the ministry's contracts): needs the military zone. */
  readonly military?: boolean;
}

/** Fictional airlines. Ids are saved; append only. */
export const AIRLINES: readonly AirlineDef[] = [
  { id: 1, name: 'Lale Hava Yolları', code: 'LH', color: 0xc8365a, fleet: [1, 3, 1] },
  { id: 2, name: 'Martı Air', code: 'MA', color: 0x2a8fd6, fleet: [3, 2, 0] },
  { id: 3, name: 'Kartal Jet', code: 'KJ', color: 0x1f2f5c, fleet: [1, 3, 2, 0, 0, 0, 0, 0, 0, 1] },
  { id: 4, name: 'Ege Wings', code: 'EW', color: 0x1fa596, fleet: [4, 1, 0] },
  {
    id: 5,
    name: 'Meridyen Airways',
    code: 'MR',
    color: 0x7a3fa0,
    fleet: [0, 2, 3, 0, 0, 0, 0, 0, 0, 2],
    international: true,
  },
  { id: 6, name: 'Poyraz Express', code: 'PX', color: 0xe8862a, fleet: [2, 3, 0] },
  {
    id: 7,
    name: 'Boğaziçi Hava Yolları',
    code: 'BH',
    color: 0xb3202a,
    fleet: [0, 3, 3, 0, 0, 0, 0, 0, 0, 3],
    international: true,
  },
  { id: 8, name: 'Anka Air', code: 'AK', color: 0xd9a31a, fleet: [3, 2, 0] },
  { id: 9, name: 'Kapadokya Jet', code: 'KP', color: 0x9b4d2e, fleet: [4, 1, 0] },
  {
    id: 10,
    name: 'Akdeniz Airlines',
    code: 'AD',
    color: 0x1d6fb8,
    fleet: [1, 3, 1],
    international: true,
  },
  { id: 11, name: 'Turna Express', code: 'TX', color: 0x3c8d3a, fleet: [3, 2, 0] },
  {
    id: 12,
    name: 'Nordic Sky',
    code: 'NS',
    color: 0x2b3f63,
    fleet: [0, 2, 2, 0, 0, 0, 0, 0, 0, 1],
    international: true,
  },
  {
    id: 13,
    name: 'Levant Airways',
    code: 'LV',
    color: 0x0f7a6b,
    fleet: [1, 2, 2, 0, 0, 0, 0, 0, 0, 1],
    international: true,
  },
  {
    id: 14,
    name: 'Danube Air',
    code: 'DA',
    color: 0x5a4fb0,
    fleet: [2, 3, 0],
    international: true,
  },
  {
    id: 15,
    name: 'Anadolu Cargo',
    code: 'AC',
    color: 0x8a5a1c,
    fleet: [2, 3, 1, 0, 0, 0, 0, 0, 0, 0, 1],
    cargo: true,
  },
  {
    id: 16,
    name: 'Bosphorus Freight',
    code: 'BF',
    color: 0x2f4f6f,
    fleet: [0, 2, 3, 0, 0, 0, 0, 0, 0, 0, 3],
    cargo: true,
  },
  {
    id: 17,
    name: 'EuroLink Cargo',
    code: 'EL',
    color: 0x6b2d5c,
    fleet: [1, 3, 2, 0, 0, 0, 0, 0, 0, 0, 2],
    cargo: true,
  },
  { id: 18, name: 'SkyBox Logistics', code: 'SB', color: 0x3f6b3a, fleet: [3, 2, 0], cargo: true },
  // Private and business jet operators: the first traffic of a new airfield.
  { id: 19, name: 'Gökyüzü Özel Jet', code: 'GJ', color: 0x23395b, fleet: [0, 0, 0, 1] },
  { id: 20, name: 'Ufuk Air Taxi', code: 'UT', color: 0xd04a2f, fleet: [0, 0, 0, 1] },
  { id: 21, name: 'Elit Business Aviation', code: 'EB', color: 0x4a4a52, fleet: [0, 0, 0, 1] },
  { id: 22, name: 'Sky Club Charter', code: 'SC', color: 0x2e8b6e, fleet: [0, 0, 0, 1] },
  // The air force's units: contracts from the ministry once the military zone stands.
  {
    id: 23,
    name: 'Hava Kuvvetleri Savaş Filosu',
    code: 'HK',
    color: 0x5d6b78,
    fleet: [0, 0, 0, 0, 1],
    military: true,
  },
  {
    id: 24,
    name: 'Hava Kuvvetleri Nakliye Filosu',
    code: 'HN',
    color: 0x6b7a5a,
    fleet: [0, 0, 0, 0, 0, 1],
    military: true,
  },
  {
    id: 25,
    name: 'Taktik İHA Filosu',
    code: 'TI',
    color: 0x4a5560,
    fleet: [0, 0, 0, 0, 0, 0, 1],
    military: true,
  },
  // Private helicopter operators: contracts once there is a helipad.
  {
    id: 26,
    name: 'Boğaz Helikopter',
    code: 'BH',
    color: 0x0f6e8c,
    fleet: [0, 0, 0, 0, 0, 0, 0, 1],
  },
  {
    id: 27,
    name: 'Anadolu Heli VIP',
    code: 'AV',
    color: 0x8a1f3d,
    fleet: [0, 0, 0, 0, 0, 0, 0, 1],
  },
  { id: 28, name: 'SkyTaxi Rotor', code: 'ST', color: 0xd4a017, fleet: [0, 0, 0, 0, 0, 0, 0, 1] },
  // Flying clubs, flight schools and air taxis with small single-engine aircraft.
  {
    id: 29,
    name: 'Kanat Aeroklüp',
    code: 'KA',
    color: 0xc0392b,
    fleet: [0, 0, 0, 0, 0, 0, 0, 0, 1],
  },
  {
    id: 30,
    name: 'Gökyüzü Uçuş Okulu',
    code: 'GU',
    color: 0x2471a3,
    fleet: [0, 0, 0, 0, 0, 0, 0, 0, 1],
  },
  {
    id: 31,
    name: 'Mavi Pervane',
    code: 'MP',
    color: 0x1abc9c,
    fleet: [0, 0, 0, 0, 0, 0, 0, 0, 1],
  },
  {
    id: 32,
    name: 'Serçe Hava Taksi',
    code: 'SH',
    color: 0xd68910,
    fleet: [0, 0, 0, 0, 0, 0, 0, 0, 1],
  },
  // A heavy-lift freight airline: the giant freighters only.
  {
    id: 33,
    name: 'Atlas Ağır Kargo',
    code: 'AA',
    color: 0x34495e,
    fleet: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1],
    cargo: true,
  },
];

/** Freight per cargo flight (tonnes, before the load factor), by aircraft class. */
export const CARGO_TONNES: Readonly<Record<AircraftClassId, number>> = {
  1: 7,
  2: 22,
  3: 65,
  4: 1,
  5: 0,
  6: 0,
  7: 0,
  8: 0,
  9: 0,
  10: 110,
  // An-124 class: the most a freighter carries.
  11: 150,
};
/** Handling charge per tonne of freight (cents), raised 25% per cargo terminal level. */
export const CARGO_FEE_PER_TONNE = dollars(90);
/** Loading or unloading time per tonne (milli-minutes), by cargo terminal level (0: none). */
export const CARGO_MILLIMINUTES_PER_TONNE: readonly number[] = [2000, 1000, 700, 500];
/** Share (percent) of offers from freight airlines once there is a cargo terminal. */
export const CARGO_OFFER_PERCENT = 35;

export function airlineById(id: number): AirlineDef | undefined {
  return AIRLINES.find((a) => a.id === id);
}

/**
 * International flights: airlines offer them only once a terminal has passport control (level
 * 2); their passengers take half as long again through check-in, security and passports, and
 * pay a higher service charge and shop more (duty free).
 */
export const INTERNATIONAL_TERMINAL_LEVEL = 2;
export const INTERNATIONAL_FEE_PERCENT = 150;
export const INTERNATIONAL_RETAIL_PERCENT = 160;

export function isInternational(airlineId: number): boolean {
  return airlineById(airlineId)?.international === true;
}

/** Contract terms offered to the player. */
export const OFFER_DAYS = 7;
export const MAX_OPEN_OFFERS = 3;
/** Offers stay open this many game hours. */
export const OFFER_LIFETIME_HOURS = 24;
/** A flight that cannot be handled within this many minutes is cancelled. */
export const MAX_HOLD_MINUTES = 120;
/** Charged when a flight is cancelled because the airport could not handle it. */
export const CANCELLATION_PENALTY = dollars(1_500);
/** Chance (per mille) that an arriving flight declares an emergency. */
export const EMERGENCY_PERMILLE = 12;
/** Runway closed after an emergency landing (inspection, clean-up), by fire station level. */
export const EMERGENCY_CLOSURE_MINUTES: readonly number[] = [45, 30, 22, 15];
/** With a wildlife control unit, this share (percent) of bird strikes never happen. */
export const WILDLIFE_AVERTED_PERCENT = 70;
/** Repairs after a bird strike, on top of any defect found (minutes). */
export const BIRD_STRIKE_REPAIR_MINUTES = 50;
/**
 * Night noise quota (22:00–06:00), as at noise-sensitive airports: every landing and take-off
 * counts quota points by aircraft class; past the allowance the local authority fines each point
 * and residents complain.
 */
export const NOISE_QUOTA = 48;
export const NOISE_POINTS: Readonly<Record<AircraftClassId, number>> = {
  1: 1,
  2: 2,
  3: 4,
  4: 1,
  // Fighters are the loudest thing on any airfield; the drone hardly heard.
  5: 6,
  6: 3,
  7: 0,
  // A helicopter is loud, but over its pad only.
  8: 1,
  // A small single-engine aircraft is hardly noticed; the four-engined giants are the loudest
  // airliners of all.
  9: 0,
  10: 6,
  11: 6,
};
export const NOISE_FINE_PER_POINT = dollars(2_500);
/** Quota points each tower level past the first adds: a busier tower, a longer night agreed. */
export const NOISE_TOWER_POINTS = 8;
/**
 * The sound insulation programme, as airports fund it for the homes under their flight paths:
 * each step paid adds this many points to the night quota, for good.
 */
export const NOISE_INSULATION_POINTS = 24;
export const NOISE_INSULATION_COST: readonly number[] = [
  dollars(60_000),
  dollars(120_000),
  dollars(240_000),
];

/** Tonight's noise quota, by the tower's level and the insulation steps paid. */
export function noiseQuota(towerLevel: number, insulation: number): number {
  return (
    NOISE_QUOTA +
    NOISE_TOWER_POINTS * Math.max(0, towerLevel - 1) +
    NOISE_INSULATION_POINTS * insulation
  );
}

/** Steps of the insulation programme the tower allows: one per level past the first. */
export function insulationSteps(towerLevel: number): number {
  return Math.min(NOISE_INSULATION_COST.length, Math.max(0, towerLevel - 1));
}
/** Online mood lost per point over the quota (see reputation.ts). */
export const NOISE_BUZZ_PER_POINT = 12;
/** First aid reaching a stand with no road from the fire station (minutes). */
export const MEDICAL_FALLBACK_MINUTES = 30;

/** An airline suspends its contract after this many flights in a row could not be handled. */
export const SUSPEND_STRIKES = 3;
/** …or when its satisfaction with the contract falls below this (after a few flights). */
export const SUSPEND_SCORE = 25;
/** Ending a contract early costs this share (percent) of each remaining flight's revenue. */
export const TERMINATION_PERCENT = 30;
/** Daily upkeep of a building, per mille of everything spent on it. */
export const UPKEEP_PER_DAY_PERMILLE = 10;

// ---------------------------------------------------------------------------------------------
// Passengers and terminals

/**
 * Passengers per minute through a jet bridge (contact stand), by stairs and bus at a level 2 stand
 * (two sets of stairs) and at a level 1 stand.
 */
export const BRIDGE_PAX_PER_MINUTE = 20;
export const FAST_STAND_PAX_PER_MINUTE = 13;
export const REMOTE_PAX_PER_MINUTE = 9;
/**
 * A level 2 stand's ground services (fuel hydrant pits, fixed ground power, its own handling
 * equipment): this share (percent) of the usual time.
 */
export const FAST_STAND_TIME_PERCENT = 75;

/** Check-in + security throughput (passengers per minute) of one terminal, by level. */
export const TERMINAL_PAX_PER_MINUTE: readonly number[] = [0, 5, 9, 14, 20, 28];

/** Average shop and food spend per departing passenger (cents), by best terminal level. */
export const RETAIL_PER_PAX: readonly number[] = [0, 200, 400, 650, 900, 1250];

/** Car park revenue per departing passenger (cents) per car-park level, capped below. */
export const PARKING_PER_PAX_PER_LEVEL = 60;
/**
 * Toll per departing passenger (cents) whose way in from the motorway passes a toll plaza
 * (several plazas on the way still charge once).
 */
export const TOLL_PER_PAX = 50;
export const PARKING_MAX_LEVELS = 6;

/** Departing passengers start arriving this long before departure, the last ones this late. */
export const SHOW_UP_START_MINUTES = 120;
export const SHOW_UP_END_MINUTES = 35;
/** After the scheduled departure, boarding waits this long for late passengers. */
export const BOARDING_GRACE_MINUTES = 15;
/** Airport rating, 0–1000 (100.0 %), starting value. */
export const INITIAL_RATING = 600;

// ---------------------------------------------------------------------------------------------
// Punctuality and airline relations

/**
 * Scheduled departure = arrival slot + this allowance for approach, landing and taxiing in +
 * the class's turnaround time. A well-run airport keeps to it; delays come from bottlenecks.
 */
export const SCHEDULE_TAXI_MINUTES = 30;
/** A departure up to this many minutes late counts as on time (as in airline statistics). */
export const ON_TIME_MINUTES = 15;
/** Airline satisfaction with a contract (0–100) when it starts. */
export const CONTRACT_INITIAL_SCORE = 80;
/** At the end of a contract, an airline this satisfied offers to renew it. */
export const RENEWAL_MIN_SCORE = 70;
/** A very satisfied airline asks for one more daily flight on renewal (up to this many). */
export const RENEWAL_BONUS_SCORE = 90;
export const MAX_FLIGHTS_PER_DAY = 4;

// ---------------------------------------------------------------------------------------------
// Maintenance

/** Chance (per mille) that a turnaround finds a technical defect, by best hangar level. */
export const DEFECT_PERMILLE: readonly number[] = [40, 25, 20, 15];
/** Repair time in game minutes by best hangar level; without one, engineers come from elsewhere. */
export const REPAIR_MINUTES: readonly number[] = [120, 35, 25, 15];
/** Line maintenance fee per departure (cents) with a hangar; +50 % per hangar level above 1. */
export const MAINTENANCE_FEE: Readonly<Record<AircraftClassId, number>> = {
  1: dollars(150),
  2: dollars(300),
  3: dollars(700),
  4: dollars(60),
  // The air force maintains its own aircraft.
  5: 0,
  6: 0,
  7: 0,
  8: dollars(90),
  9: dollars(40),
  10: dollars(1_100),
  11: dollars(1_300),
};

// ---------------------------------------------------------------------------------------------
// Aircraft parking: night stops and parking rentals

/** Airport charge per hour an aircraft stands on a remote parking position (cents, by class). */
export const AIRCRAFT_PARKING_PER_HOUR: Readonly<Record<AircraftClassId, number>> = {
  1: dollars(60),
  2: dollars(110),
  3: dollars(240),
  4: dollars(30),
  5: dollars(0),
  6: dollars(0),
  7: dollars(0),
  8: dollars(0),
  9: dollars(10),
  10: dollars(400),
  11: dollars(450),
};
/** Share of late-evening passenger arrivals that stay the night and leave in the morning. */
export const NIGHT_STOP_PERCENT = 55;
/** Arrivals from this minute of the day (and up to NIGHT_STOP_UNTIL after midnight) may stay. */
export const NIGHT_STOP_FROM = 21 * 60;
export const NIGHT_STOP_UNTIL = 60;
/** A night stop leaves the next morning between these minutes of the day. */
export const NIGHT_STOP_DEPART_FROM = 6 * 60;
export const NIGHT_STOP_DEPART_SPREAD = 150;
/** An aircraft is towed back to a stand this long before it leaves (minutes). */
export const TOW_BACK_MINUTES = 120;
/** Towed off its stand only if it would stand idle at least this long (minutes). */
export const PARK_MIN_MINUTES = 150;
/** Share of airline offers that are a parking rental, while a remote position is free. */
export const RENTAL_OFFER_PERCENT = 25;
/** Days an aircraft is parked under a rental. */
export const RENTAL_DAYS_MIN = 2;
export const RENTAL_DAYS_MAX = 5;
